/**
 * Conversation Panel
 *
 * Full message thread UI with streaming responses, feedback thumbs,
 * auto-scroll, and send/stop input bar.
 */

import { Icon, IconKey } from '@/shared/components/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Textarea } from '@/shared/components/ui/textarea';
import type { AIMessage } from '@/shared/types';
import { cn } from '@/shared/utils/cn';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useAIChat } from '../context';
import { aiKeys, useConversation, useSubmitFeedback } from '../hooks/use-ai';
import { aiService } from '../services/ai-service';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConversationPanelProps {
  conversationId: string | null;
}

// ---------------------------------------------------------------------------
// Markdown renderer (inline utility)
// ---------------------------------------------------------------------------

function renderMarkdown(text: string): ReactNode {
  // Split on triple-backtick code blocks
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...renderParagraphs(text.slice(lastIndex, match.index), key));
      key += 1000;
    }
    parts.push(
      <pre
        key={`code-${key++}`}
        className="bg-secondary/70 rounded-md px-3 py-2 my-1.5 overflow-x-auto text-xs"
      >
        <code>{match[2]}</code>
      </pre>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...renderParagraphs(text.slice(lastIndex), key));
  }

  return parts;
}

function renderParagraphs(text: string, baseKey: number): ReactNode[] {
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, i) => {
    const trimmed = para.trim();
    if (!trimmed) return null;

    // Check if this is a list block
    const lines = trimmed.split('\n');
    const isListBlock = lines.every(
      (l) => l.trim().startsWith('- ') || l.trim().startsWith('* ') || l.trim() === '',
    );

    if (isListBlock) {
      const items = lines.filter((l) => l.trim().startsWith('- ') || l.trim().startsWith('* '));
      return (
        <ul key={`p-${baseKey}-${i}`} className="list-disc pl-4 my-1 space-y-0.5">
          {items.map((item, j) => (
            <li key={j} className="text-sm">
              {renderInline(item.replace(/^[\s]*[-*]\s/, ''))}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={`p-${baseKey}-${i}`} className="text-sm my-1">
        {renderInline(trimmed)}
      </p>
    );
  });
}

function renderInline(text: string): ReactNode {
  // Replace **bold**, `code`, and \n → <br />
  const parts: ReactNode[] = [];
  // Combined regex for bold, inline code, and line breaks
  const inlineRegex = /(\*\*(.+?)\*\*)|(`([^`]+?)`)|(\n)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // Bold
      parts.push(<strong key={`i-${key++}`}>{match[2]}</strong>);
    } else if (match[3]) {
      // Inline code
      parts.push(
        <code
          key={`i-${key++}`}
          className="bg-secondary/70 rounded px-1 py-0.5 text-xs font-mono"
        >
          {match[4]}
        </code>,
      );
    } else if (match[5]) {
      // Line break
      parts.push(<br key={`i-${key++}`} />);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length === 1 ? parts[0] : parts;
}

// ---------------------------------------------------------------------------
// Sub-components (not exported)
// ---------------------------------------------------------------------------

function ConversationPanelHeader({ title }: { title: string | null | undefined }) {
  return (
    <div className="px-4 py-2 border-b border-border/30">
      <p className="text-xs text-text/50 truncate">{title ?? 'New conversation'}</p>
    </div>
  );
}

function MessageBubble({
  message,
  onFeedback,
}: {
  message: AIMessage;
  onFeedback: (id: string, val: -1 | 0 | 1) => void;
}) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'px-3 py-2 max-w-[85%]',
          isUser
            ? 'bg-accent/15 rounded-2xl rounded-br-md ml-auto'
            : 'bg-secondary/50 rounded-2xl rounded-bl-md',
        )}
      >
        {renderMarkdown(message.content)}
      </div>
      {!isUser && (
        <div className="flex items-center gap-1 mt-0.5 ml-1">
          <button
            type="button"
            onClick={() =>
              onFeedback(message.id, message.feedback === 1 ? 0 : 1)
            }
            className={cn(
              'p-1 rounded-md hover:bg-secondary/50 transition-colors',
              message.feedback === 1 ? 'text-accent' : 'text-text/30 hover:text-text/50',
            )}
          >
            <Icon icon={IconKey.ThumbsUp} size={12} />
          </button>
          <button
            type="button"
            onClick={() =>
              onFeedback(message.id, message.feedback === -1 ? 0 : -1)
            }
            className={cn(
              'p-1 rounded-md hover:bg-secondary/50 transition-colors',
              message.feedback === -1 ? 'text-accent' : 'text-text/30 hover:text-text/50',
            )}
          >
            <Icon icon={IconKey.ThumbsDown} size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function StreamingBubble({ content, isStreaming }: { content: string; isStreaming: boolean }) {
  return (
    <div className="flex flex-col items-start">
      <div className="bg-secondary/50 rounded-2xl rounded-bl-md px-3 py-2 max-w-[85%]">
        {content ? (
          <>
            {renderMarkdown(content)}
            {isStreaming && (
              <span className="inline-block w-1.5 h-4 bg-accent/60 animate-cursor-blink ml-0.5 align-middle" />
            )}
          </>
        ) : (
          <div className="flex items-center gap-1 py-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-text/40 animate-pulse" />
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-text/40 animate-pulse [animation-delay:150ms]" />
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-text/40 animate-pulse [animation-delay:300ms]" />
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div className="bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-md px-3 py-2 mx-3 flex items-center justify-between gap-2">
      <span>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 p-0.5 hover:bg-destructive/10 rounded"
      >
        <Icon icon={IconKey.X} size={12} />
      </button>
    </div>
  );
}

function MessageInput({
  value,
  onChange,
  onSubmit,
  isStreaming,
  onStop,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  isStreaming: boolean;
  onStop: () => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    autoResize(e.target);
  };

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Reset height when value is cleared
  useEffect(() => {
    if (!value && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value]);

  return (
    <div className="px-3 py-2 flex items-end gap-2 shrink-0">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder="Ask about your finances..."
        rows={1}
        className="flex-1 resize-none bg-muted dark:bg-background text-sm text-text placeholder:text-text/40 outline-none max-h-[120px] overflow-y-auto py-1.5"
      />

      {isStreaming ? (
        <button
          type="button"
          onClick={onStop}
          className="shrink-0 p-1.5 rounded-md text-text/60 hover:text-text hover:bg-secondary/50 transition-colors"
        >
          <Icon icon={IconKey.Square} size={16} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value.trim()}
          className="shrink-0 p-1.5 rounded-md text-text/60 hover:text-text hover:bg-secondary/50 transition-colors disabled:opacity-30 disabled:pointer-events-none"
        >
          <Icon icon={IconKey.Send} size={16} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="flex-1 p-4 space-y-4">
      {/* Pair 1: user (right) + assistant (left) */}
      <div className="flex justify-end">
        <Skeleton className="h-10 w-3/5 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-14 w-4/5 rounded-2xl rounded-bl-md" />
      </div>
      {/* Pair 2 */}
      <div className="flex justify-end">
        <Skeleton className="h-8 w-2/5 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-16 w-3/4 rounded-2xl rounded-bl-md" />
      </div>
      {/* Pair 3 */}
      <div className="flex justify-end">
        <Skeleton className="h-10 w-1/2 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-12 w-2/3 rounded-2xl rounded-bl-md" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ConversationPanel({ conversationId }: ConversationPanelProps) {
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevMessageCountRef = useRef<number>(0);
  const messagesLengthRef = useRef<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { activateConversation } = useAIChat();

  useEffect(() => {
    prevMessageCountRef.current = 0;
    setStreamingContent('');
    setOptimisticUserMessage(null);
    setIsStreaming(false);
    setError(null);
  }, [conversationId]);

  const { data, isLoading } = useConversation(conversationId);
  const queryClient = useQueryClient();
  const feedbackMutation = useSubmitFeedback(conversationId ?? '');
  const messages = data?.messages.filter((m) => m.role !== 'tool') ?? [];
  messagesLengthRef.current = messages.length;

  // -------------------------------------------------------------------------
  // sendMessage
  // -------------------------------------------------------------------------

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming || !content.trim()) return;

      prevMessageCountRef.current = messagesLengthRef.current;
      setOptimisticUserMessage(content);
      setInput('');
      setIsStreaming(true);
      setStreamingContent('');
      setError(null);

      let targetId = conversationId;

      // Draft mode: create conversation first
      if (!targetId) {
        try {
          const newConv = await aiService.createConversation();
          targetId = newConv.id;
          activateConversation(targetId);
          queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to create conversation');
          setIsStreaming(false);
          return;
        }
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        for await (const event of aiService.streamMessage(
          targetId,
          content,
          controller.signal,
        )) {
          switch (event.type) {
            case 'text_delta':
              setStreamingContent((prev) => prev + event.content);
              break;
            case 'tool_call':
              // no-op
              break;
            case 'done':
              break;
            case 'error':
              setError(event.message);
              break;
          }
          if (event.type === 'done' || event.type === 'error') break;
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Silently stop
        } else {
          setError(err instanceof Error ? err.message : 'Something went wrong');
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
        queryClient.invalidateQueries({
          queryKey: aiKeys.conversation(targetId),
        });
      }
    },
    [isStreaming, conversationId, queryClient, activateConversation],
  );

  // -------------------------------------------------------------------------
  // cancelStream
  // -------------------------------------------------------------------------

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  // -------------------------------------------------------------------------
  // Auto-scroll
  // -------------------------------------------------------------------------

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingContent, messages.length]);

  // -------------------------------------------------------------------------
  // Clear optimistic state once server data arrives
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      prevMessageCountRef.current = messages.length;
      if (optimisticUserMessage !== null) {
        setOptimisticUserMessage(null);
      }
      const lastMessage = messages[messages.length - 1];
      if (streamingContent && !isStreaming && lastMessage?.role === 'assistant') {
        setStreamingContent('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, optimisticUserMessage, streamingContent, isStreaming]);

  // -------------------------------------------------------------------------
  // Feedback handler
  // -------------------------------------------------------------------------

  const handleFeedback = useCallback(
    (messageId: string, val: -1 | 0 | 1) => {
      feedbackMutation.mutate({ messageId, feedback: val });
    },
    [feedbackMutation],
  );

  // -------------------------------------------------------------------------
  // Submit handler for MessageInput
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(() => {
    sendMessage(input);
  }, [sendMessage, input]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ConversationPanelHeader title={data?.title} />

      {isLoading && conversationId ? (
        <LoadingSkeleton />
      ) : (
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3"
        >
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} onFeedback={handleFeedback} />
          ))}

          {optimisticUserMessage && (
            <div className="flex flex-col items-end">
              <div className="bg-accent/15 rounded-2xl rounded-br-md px-3 py-2 max-w-[85%] ml-auto">
                <p className="text-sm">{optimisticUserMessage}</p>
              </div>
            </div>
          )}

          {(isStreaming || streamingContent) && <StreamingBubble content={streamingContent} isStreaming={isStreaming} />}

          <div ref={messagesEndRef} />
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <MessageInput
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        isStreaming={isStreaming}
        onStop={cancelStream}
      />
    </div>
  );
}
